import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import GradingIcon from '@mui/icons-material/Grading';
import { resources } from '../../core/services/resources';
import { extractJobErrorMessage } from '../../core/services/api';
import { ConfirmDialog } from '../../shared/components/Dialogs/ConfirmDialog';
import {
  CopyMarkdownButton,
  DocumentSectionList,
  ExpandAllSectionsButton,
  buildDocumentMarkdown,
  useExpandAll,
} from '../../shared/components/DocumentSections';
import { GlassPanel } from '../../shared/components/GlassPanel';
import { IconTile } from '../../shared/components/IconTile';
import { SourcesBlock } from '../../shared/components/SourcesBlock';
import { WizardStepper } from '../../shared/components/WizardStepper';
import { useLocalStorageState } from '../../shared/hooks/useLocalStorageState';
import { usePermissions } from '../../shared/hooks/usePermissions';
import { GenerationProgressPanel } from '../projects/dashboard/GenerationProgressPanel';
import { JOB_TYPE_LABEL } from '../projects/dashboard/jobDisplay';
import { ProjectMembersHeader } from '../projects/ProjectMembersHeader';
import { getProjectTypeProfile, isWizardOnlyProject } from '../onboarding/projectTypeProfiles';
import type { DocumentSection, Job, ProjectType } from '../../core/types/data-models';
import {
  INTRO_STEP_KEY,
  SUMMARY_STEP_KEY,
  buildValidationSteps,
  buildValidationStages,
  jobDocumentType,
  type IntroStepState,
  type ValidationStage,
} from './validationStages';
import { hasSourceBrief, validationCopyFor } from './validationCopy';
import { IntroPanel } from './IntroPanel';
import { ValidationSectionRow } from './ValidationSectionRow';
import { ValidationSummary } from './ValidationSummary';
import { EditSectionDialog } from './EditSectionDialog';

/** The background jobs of the journey besides the stages' generation, detected among the project's active jobs. */
const SUMMARY_JOB_TYPE = 'GENERATE_VALIDATION_SUMMARY';
const ANALYZE_JOB_TYPE = 'ANALYZE_PROJECT_SOURCES';

/**
 * `/projects/:id/validation` — the guided validation, continuing the
 * onboarding wizard's progress line: one document per stage of the project
 * type's validation category (IDEA_VALIDATION for an idea, BUSINESS_VALIDATION
 * for a company), generated in catalog order, then a summary. A business
 * project starts by analyzing its sources into a company brief, which the
 * page shows as the first step. Shows the running generation's progress,
 * then the generated document's sections with their verdict score and
 * confidence, and a "Next step" that generates the following stage — or,
 * with "Run automatically" on, runs every remaining step up to the summary.
 */
export function ValidationPage() {
  const { id } = useParams();
  const projectId = Number(id);
  const queryClient = useQueryClient();
  const { canEdit } = usePermissions();

  const projectQuery = useQuery({
    queryKey: ['projects', projectId],
    queryFn: () => resources.projects.get(projectId),
    enabled: Number.isFinite(projectId),
  });
  const docTypesQuery = useQuery({
    queryKey: ['documentTypes'],
    queryFn: () => resources.documentTypes.list({ pageSize: 500, sort: 'order' }),
  });
  // Same key and params as the project dashboard, so the two share one cache entry.
  const docsQuery = useQuery({
    queryKey: ['documents', { projectId }],
    queryFn: () => resources.documents.list({ pageSize: 100, filter: { projectId }, sort: '-updatedAt' }),
    enabled: Number.isFinite(projectId),
  });
  const activeJobsQuery = useQuery({
    queryKey: ['project-jobs-active', projectId],
    queryFn: () =>
      resources.jobs.list({
        filter: { projectId: [projectId], status: ['PENDING', 'PROCESSING'] },
        pageSize: 20,
        sort: '-id',
      }),
    enabled: Number.isFinite(projectId),
    refetchInterval: (query) => ((query.state.data?.data?.length ?? 0) > 0 ? 2500 : false),
  });
  const historyQuery = useQuery({
    queryKey: ['project-jobs-history', projectId],
    queryFn: () => resources.jobs.list({ filter: { projectId: [projectId] }, pageSize: 20, sort: '-id' }),
    enabled: Number.isFinite(projectId),
  });
  // Same key as the dashboard's verdict chip, so the two share one cache entry.
  const summaryQuery = useQuery({
    queryKey: ['validation-summary', projectId],
    queryFn: () => resources.validationSummaryFor(projectId),
    enabled: Number.isFinite(projectId),
  });

  // The wording and the stages follow the project type; until the project
  // is loaded the page is a spinner, so the default type never shows.
  const project = projectQuery.data;
  const projectType: ProjectType = project?.projectType ?? 'IDEA_VALIDATION';
  const profile = getProjectTypeProfile(projectType);
  const copy = validationCopyFor(projectType);

  const stages = useMemo(
    () => buildValidationStages(docTypesQuery.data?.data ?? [], docsQuery.data?.data ?? [], profile.validationCategory),
    [docTypesQuery.data, docsQuery.data, profile.validationCategory],
  );
  const stageByType = useMemo(() => new Map(stages.map((s) => [s.docType.type, s])), [stages]);

  const activeJobs = activeJobsQuery.data?.data ?? [];
  const generatingJob =
    activeJobs.find((j) => j.jobType === 'GENERATE_DOCUMENT' && stageByType.has(jobDocumentType(j) ?? '')) ?? null;
  const generatingType = generatingJob ? jobDocumentType(generatingJob) : null;
  const summaryJob = activeJobs.find((j) => j.jobType === SUMMARY_JOB_TYPE) ?? null;
  const analyzeJob = activeJobs.find((j) => j.jobType === ANALYZE_JOB_TYPE) ?? null;
  const otherBusyJob = activeJobs.find((j) => j !== generatingJob && j !== summaryJob && j !== analyzeJob) ?? null;
  const isBusy = activeJobs.length > 0;

  // When the active set drains, the documents and sections changed underneath us.
  const wasBusy = useRef(false);
  useEffect(() => {
    if (wasBusy.current && !isBusy) {
      queryClient.invalidateQueries({ queryKey: ['documents', { projectId }] });
      queryClient.invalidateQueries({ queryKey: ['document-sections'] });
      queryClient.invalidateQueries({ queryKey: ['sidebar-documents'] });
      queryClient.invalidateQueries({ queryKey: ['validation-summary', projectId] });
      historyQuery.refetch();
    }
    wasBusy.current = isBusy;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBusy]);

  const generatedStages = stages.filter((s) => s.document);
  const lastGenerated = generatedStages[generatedStages.length - 1] ?? null;
  const nextStage = stages.find((s) => !s.document) ?? null;
  const allDone = stages.length > 0 && nextStage === null;

  const summary = summaryQuery.data;
  const summaryReady = summary?.status === 'READY';
  const summaryRunning = summaryJob !== null;

  // The first step: an idea is written at creation; a company's sources
  // must be analyzed into the brief first, which the page can (re)start.
  const needsAnalysis = profile.start === 'analyze-sources';
  const introDone = !needsAnalysis || hasSourceBrief(project?.userPrompt);
  const introState: IntroStepState = introDone ? 'done' : analyzeJob ? 'running' : 'active';
  const analysisFirst = needsAnalysis && !introDone && generatedStages.length === 0;

  // What the page shows: the user's pick (the first step, a stage type or
  // the summary), else the step a link asked for, else the stage being
  // generated, else the first step while the sources are analyzed, else the
  // summary while it is built or once it is ready, else the latest
  // generated stage.
  const [searchParams] = useSearchParams();
  const requestedStep = searchParams.get('step') === SUMMARY_STEP_KEY ? SUMMARY_STEP_KEY : null;
  const [selection, setSelection] = useState<string | null>(null);
  const selectedKey =
    selection ??
    requestedStep ??
    generatingType ??
    (analyzeJob ? INTRO_STEP_KEY : null) ??
    (summaryRunning || (allDone && summaryReady) ? SUMMARY_STEP_KEY : null) ??
    lastGenerated?.docType.type ??
    null;
  const showSummary = selectedKey === SUMMARY_STEP_KEY;
  const showIntro = selectedKey === INTRO_STEP_KEY;
  const selectedStage: ValidationStage | null =
    !showSummary && !showIntro && selectedKey ? stageByType.get(selectedKey) ?? null : null;
  const selectedDoc = selectedStage?.document ?? null;

  // Once the analysis lands, the project's prompt changed and the chained
  // first stage is what to look at — drop a pick of the first step made
  // while it ran, so the default applies again.
  const wasAnalyzing = useRef(false);
  useEffect(() => {
    if (wasAnalyzing.current && !analyzeJob) {
      queryClient.invalidateQueries({ queryKey: ['projects', projectId] });
      historyQuery.refetch();
      setSelection((current) => (current === INTRO_STEP_KEY ? null : current));
    }
    wasAnalyzing.current = analyzeJob !== null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analyzeJob]);

  const steps = useMemo(
    () =>
      buildValidationSteps(stages, {
        intro: { label: copy.intro.stepLabel, state: introState, clickable: true },
        generatingType,
        summary: { ready: summaryReady, running: summaryRunning },
      }),
    [stages, copy, introState, generatingType, summaryReady, summaryRunning],
  );
  // Nothing generated or running yet: the first stage is what comes next —
  // unless the first step itself is still to do.
  const activeStep = selectedKey
    ? steps.findIndex((s) => s.key === selectedKey)
    : introDone
      ? Math.min(1, steps.length - 1)
      : 0;

  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);
  const [regenerateOpen, setRegenerateOpen] = useState(false);
  const [dismissedFailedJobId, setDismissedFailedJobId] = useState<number | null>(null);

  const startStage = async (stage: ValidationStage) => {
    if (!project) return;
    setStarting(true);
    setStartError(null);
    try {
      // Same prompt for every stage — the idea the project was created
      // from, or the company brief — so it stays the project context (the
      // job also reads it back from the project when the document type asks
      // for it).
      await resources.generateDocument(projectId, {
        documentType: stage.docType.type,
        generateUserPrompt: project.generateUserPrompt ?? project.userPrompt ?? '',
        // A stage is read here, not on a dashboard: skip the follow-up job
        // that builds dashboard cards, so the next step unlocks sooner.
        // Entities are still extracted.
        skipDashboard: true,
      });
      setSelection(stage.docType.type);
      await activeJobsQuery.refetch();
    } catch (e) {
      setStartError(extractJobErrorMessage(e));
    } finally {
      setStarting(false);
    }
  };

  // Same job as the first run: it finds the project's document of this type
  // and rewrites its sections in place, so the stage keeps its document.
  const regenerate = async () => {
    if (!selectedStage) return;
    await startStage(selectedStage);
    setRegenerateOpen(false);
  };

  // Re-scores the stage's questions from their saved content, without
  // regenerating it; the rows pick up the new scores once the job drains.
  const rescore = async () => {
    if (!selectedDoc) return;
    setStarting(true);
    setStartError(null);
    try {
      await resources.scoreDocumentSections(selectedDoc.id);
      await activeJobsQuery.refetch();
    } catch (e) {
      setStartError(extractJobErrorMessage(e));
    } finally {
      setStarting(false);
    }
  };

  // Reads the sources into the company brief; the job then generates the
  // first stage itself while that stage has no document yet.
  const startAnalysis = async () => {
    setStarting(true);
    setStartError(null);
    try {
      await resources.analyzeProjectSources(projectId);
      setSelection(INTRO_STEP_KEY);
      await activeJobsQuery.refetch();
    } catch (e) {
      setStartError(extractJobErrorMessage(e));
    } finally {
      setStarting(false);
    }
  };

  // Builds (or rebuilds) the summary from the stage documents; the row keeps
  // its previous content until the job replaces it.
  const startSummary = async () => {
    setStarting(true);
    setStartError(null);
    try {
      await resources.generateValidationSummary(projectId);
      setSelection(SUMMARY_STEP_KEY);
      await Promise.all([activeJobsQuery.refetch(), summaryQuery.refetch()]);
    } catch (e) {
      setStartError(extractJobErrorMessage(e));
    } finally {
      setStarting(false);
    }
  };

  // The header's one action: the next stage to generate; then, once every
  // stage exists, building the summary; then opening it. Generating (a stage
  // or the summary) needs edit rights; opening the summary does not. While
  // the sources still have to be analyzed, the body panels carry that action.
  const nextAction = analysisFirst
    ? null
    : nextStage
      ? {
          label: generatedStages.length === 0 ? copy.start.button : `Next step: ${nextStage.docType.name}`,
          disabled: isBusy || starting,
          needsEdit: true,
          run: () => void startStage(nextStage),
        }
      : allDone && !summaryReady && !summaryRunning
        ? {
            label: summary?.status === 'FAILED' ? 'Retry summary' : 'Generate summary',
            disabled: isBusy || starting,
            needsEdit: true,
            run: () => void startSummary(),
          }
        : allDone && summaryReady && !showSummary
          ? { label: 'Summary', disabled: false, needsEdit: false, run: () => setSelection(SUMMARY_STEP_KEY) }
          : null;
  // Before anything exists the stage action lives in the body panel instead —
  // unless the first step is shown in that panel's place.
  const showHeaderAction =
    nextAction !== null &&
    (!nextAction.needsEdit || canEdit) &&
    (nextStage ? generatedStages.length > 0 || showIntro : true);

  // The newest generation job of each stage (history is newest first): a
  // failed first run or a failed regeneration surfaces until a later run of
  // that stage replaces it. Hidden while a retry runs or once dismissed.
  const history = historyQuery.data?.data ?? [];
  const latestGenerationJobs = new Map<string, Job>();
  for (const j of history) {
    const type = j.jobType === 'GENERATE_DOCUMENT' ? jobDocumentType(j) : null;
    if (type && stageByType.has(type) && !latestGenerationJobs.has(type)) latestGenerationJobs.set(type, j);
  }
  const failedJob = generatingJob
    ? null
    : Array.from(latestGenerationJobs.values()).find((j) => j.status === 'FAILED' && j.id !== dismissedFailedJobId) ??
      null;
  const failedStage = failedJob ? stageByType.get(jobDocumentType(failedJob) ?? '') ?? null : null;
  // The summary's own job, when its latest run failed: surfaces until a later
  // run replaces it (the row points at its newest job) or it is dismissed.
  const failedSummaryJob =
    summaryJob || !summary || summary.status !== 'FAILED'
      ? null
      : history.find(
          (j) => j.jobType === SUMMARY_JOB_TYPE && j.id === summary.jobId && j.status === 'FAILED' && j.id !== dismissedFailedJobId,
        ) ?? null;
  // The newest source analysis, when it failed and no later one runs.
  const latestAnalyzeJob = history.find((j) => j.jobType === ANALYZE_JOB_TYPE) ?? null;
  const failedAnalyzeJob =
    !analyzeJob && latestAnalyzeJob?.status === 'FAILED' && latestAnalyzeJob.id !== dismissedFailedJobId
      ? latestAnalyzeJob
      : null;

  // "Run automatically": whenever nothing runs, start the journey's next
  // step — the source analysis, the next stage, then the summary — until the
  // summary is shown. Stops on any failure until the user retries. Each step
  // starts at most once per visit: right after a job drains, the documents
  // (or the summary row) are still being refetched and would otherwise make
  // the step just finished look missing.
  const [autoRun, setAutoRun] = useLocalStorageState<boolean>('validation.autoRun', false);
  const autoStarted = useRef(new Set<string>());
  const autoRunReady =
    autoRun &&
    canEdit &&
    !isBusy &&
    !starting &&
    !startError &&
    !failedJob &&
    !failedSummaryJob &&
    !failedAnalyzeJob &&
    projectQuery.isSuccess &&
    docTypesQuery.isSuccess &&
    docsQuery.isSuccess &&
    activeJobsQuery.isSuccess &&
    historyQuery.isSuccess &&
    summaryQuery.isSuccess;
  useEffect(() => {
    if (!autoRunReady) return;
    const startOnce = (key: string, start: () => void) => {
      if (autoStarted.current.has(key)) return;
      autoStarted.current.add(key);
      start();
    };
    if (analysisFirst) {
      startOnce(INTRO_STEP_KEY, () => void startAnalysis());
    } else if (nextStage) {
      startOnce(nextStage.docType.type, () => void startStage(nextStage));
    } else if (allDone && !summaryReady && !summaryRunning && summary?.status !== 'FAILED') {
      startOnce(SUMMARY_STEP_KEY, () => void startSummary());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRunReady, analysisFirst, nextStage, allDone, summaryReady, summaryRunning, summary?.status]);

  const sectionsQuery = useQuery({
    queryKey: ['document-sections', selectedDoc?.id],
    queryFn: () =>
      resources.documentSections.list({ filter: { documentId: [selectedDoc!.id] }, sort: 'order', pageSize: 200 }),
    enabled: selectedDoc != null,
  });
  const { roots, childrenByParent } = useMemo(() => {
    const sections = sectionsQuery.data?.data ?? [];
    const childrenByParent = new Map<number, DocumentSection[]>();
    const roots: DocumentSection[] = [];
    for (const s of sections) {
      if (s.parentDocumentSectionId == null) {
        roots.push(s);
      } else {
        const list = childrenByParent.get(s.parentDocumentSectionId) ?? [];
        list.push(s);
        childrenByParent.set(s.parentDocumentSectionId, list);
      }
    }
    roots.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    return { roots, childrenByParent };
  }, [sectionsQuery.data]);

  // Rows start open, so the toggle starts as "collapse"; picking another stage starts over.
  const { allExpanded, expandSignal, toggleAll } = useExpandAll({ defaultExpanded: true, resetKey: selectedDoc?.id });
  const [editing, setEditing] = useState<DocumentSection | null>(null);

  if (!Number.isFinite(projectId)) {
    return <Alert severity="error">Invalid project id.</Alert>;
  }

  // Once every stage exists the default step depends on the summary row, so
  // wait for it too rather than landing on the last stage and jumping.
  const loading =
    projectQuery.isLoading || docTypesQuery.isLoading || docsQuery.isLoading || (allDone && summaryQuery.isLoading);
  const showProgress = selectedStage != null && generatingType === selectedStage.docType.type;
  const summaryStatusLabel = summaryRunning ? 'Summary · building' : summaryReady ? 'Summary' : 'Summary · not built yet';
  const subtitle = showSummary
    ? summaryStatusLabel
    : showIntro
      ? copy.intro.title
      : selectedStage
        ? `Stage ${selectedStage.index} of ${stages.length}: ${selectedStage.docType.name}`
        : null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, flex: 1, minHeight: 0 }}>
      {/* Top panel: title, progress line, next step */}
      <GlassPanel sx={{ p: { xs: 2, md: 3 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ md: 'center' }}
        >
          <Stack direction="row" spacing={1.5} alignItems="flex-start">
            <IconTile icon={<FactCheckOutlinedIcon fontSize="inherit" />} color="primary" size="md" />
            <Box>
              <Typography variant="h5" component="h1" fontWeight={800} letterSpacing="-0.01em">
                {copy.title}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                {project?.name ?? '…'}
                {subtitle ? ` · ${subtitle}` : ''}
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            {/* Wizard-only projects have no dashboard, so the invite control lives here instead. */}
            {project && isWizardOnlyProject(projectType) && <ProjectMembersHeader projectId={projectId} />}
            {canEdit && !(allDone && summaryReady) && (
              <FormControlLabel
                control={<Checkbox size="small" checked={autoRun} onChange={(e) => setAutoRun(e.target.checked)} />}
                label="Run automatically"
                slotProps={{ typography: { variant: 'body2' } }}
              />
            )}
            {showHeaderAction && nextAction && (
              <Button
                variant="contained"
                endIcon={<ArrowForwardIcon />}
                disabled={nextAction.disabled}
                onClick={nextAction.run}
              >
                {nextAction.label}
              </Button>
            )}
          </Stack>
        </Stack>

        <Box sx={{ mt: 3 }}>
          <WizardStepper steps={steps} activeStep={activeStep} onStepClick={setSelection} />
        </Box>

        {otherBusyJob && !generatingJob && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary">
              Finishing up: {JOB_TYPE_LABEL[otherBusyJob.jobType] ?? otherBusyJob.jobType}… The next step unlocks
              when it completes.
            </Typography>
            <LinearProgress sx={{ mt: 0.5, borderRadius: 4 }} />
          </Box>
        )}
      </GlassPanel>

      {startError && (
        <Alert severity="error" onClose={() => setStartError(null)}>
          {startError}
        </Alert>
      )}
      {failedAnalyzeJob && (
        <Alert
          severity="error"
          onClose={() => setDismissedFailedJobId(failedAnalyzeJob.id)}
          action={
            canEdit ? (
              <Button color="inherit" size="small" disabled={isBusy || starting} onClick={() => void startAnalysis()}>
                Retry
              </Button>
            ) : undefined
          }
        >
          Analyzing the company sources failed: {failedAnalyzeJob.error ?? 'unknown error'}
        </Alert>
      )}
      {failedJob && failedStage && (
        <Alert
          severity="error"
          onClose={() => setDismissedFailedJobId(failedJob.id)}
          action={
            canEdit ? (
              <Button
                color="inherit"
                size="small"
                disabled={isBusy || starting}
                onClick={() => void startStage(failedStage)}
              >
                Retry
              </Button>
            ) : undefined
          }
        >
          Generating {failedStage.docType.name} failed: {failedJob.error ?? 'unknown error'}
        </Alert>
      )}
      {failedSummaryJob && (
        <Alert
          severity="error"
          onClose={() => setDismissedFailedJobId(failedSummaryJob.id)}
          action={
            canEdit ? (
              <Button color="inherit" size="small" disabled={isBusy || starting} onClick={() => void startSummary()}>
                Retry
              </Button>
            ) : undefined
          }
        >
          Building the summary failed: {failedSummaryJob.error ?? 'unknown error'}
        </Alert>
      )}

      {/* Body: the first step, summary, progress, the selected stage's sections, or the start panel */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : showIntro && project ? (
        <IntroPanel
          project={project}
          copy={copy.intro}
          analyzeJob={analyzeJob}
          canAnalyze={needsAnalysis && canEdit}
          busy={isBusy || starting}
          onAnalyze={() => void startAnalysis()}
        />
      ) : showSummary ? (
        <ValidationSummary
          projectId={projectId}
          stages={stages}
          copy={copy}
          summary={summaryQuery.isLoading ? undefined : (summary ?? null)}
          summaryJob={summaryJob}
          activeJobs={activeJobs}
          showProjectInsights={isWizardOnlyProject(projectType)}
          canEdit={canEdit}
          busy={isBusy || starting}
          onGenerate={() => void startSummary()}
          onOpenStage={setSelection}
        />
      ) : showProgress ? (
        <GenerationProgressPanel job={generatingJob ?? undefined} title={`Generating ${selectedStage!.docType.name}…`} />
      ) : selectedDoc ? (
        <GlassPanel sx={{ p: { xs: 1.5, md: 3 } }}>
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6" fontWeight={700}>
                {selectedDoc.name}
              </Typography>
              {selectedDoc.description && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {selectedDoc.description}
                </Typography>
              )}
            </Box>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ flexShrink: 0 }}>
              {canEdit && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AutorenewIcon />}
                  disabled={isBusy || starting}
                  onClick={() => setRegenerateOpen(true)}
                >
                  Regenerate
                </Button>
              )}
              {canEdit && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<GradingIcon />}
                  disabled={isBusy || starting}
                  onClick={() => void rescore()}
                >
                  Re-score
                </Button>
              )}
              <CopyMarkdownButton markdown={buildDocumentMarkdown(selectedDoc.name, roots, childrenByParent)} />
              <ExpandAllSectionsButton allExpanded={allExpanded} onToggle={toggleAll} />
            </Stack>
          </Stack>
          <Box sx={{ mt: 2 }}>
            {sectionsQuery.isLoading ? (
              <CircularProgress size={20} />
            ) : roots.length === 0 ? (
              <Typography variant="body2" color="text.disabled">
                This document has no sections yet.
              </Typography>
            ) : (
              <DocumentSectionList>
                {roots.map((section) => (
                  <ValidationSectionRow
                    key={section.id}
                    section={section}
                    childrenByParent={childrenByParent}
                    expandSignal={expandSignal}
                    canEdit={canEdit}
                    onEdit={setEditing}
                  />
                ))}
              </DocumentSectionList>
            )}
          </Box>
          {/* Keyed by document so picking another stage starts collapsed again. */}
          <SourcesBlock key={selectedDoc.id} sources={selectedDoc.citations} sx={{ mt: 2 }} />
        </GlassPanel>
      ) : analysisFirst ? (
        <GlassPanel
          sx={{ p: 6, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, textAlign: 'center' }}
        >
          <AutoAwesomeIcon sx={{ fontSize: 40, color: 'primary.main' }} />
          <Typography variant="h6" fontWeight={700}>
            {copy.start.analyzeTitle}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 520 }}>
            {copy.start.analyzeBody}
          </Typography>
          {canEdit && (
            <Button
              variant="contained"
              size="large"
              startIcon={<AutoAwesomeIcon />}
              disabled={isBusy || starting}
              onClick={() => void startAnalysis()}
            >
              {copy.start.analyzeButton}
            </Button>
          )}
        </GlassPanel>
      ) : (
        <GlassPanel
          sx={{ p: 6, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, textAlign: 'center' }}
        >
          <AutoAwesomeIcon sx={{ fontSize: 40, color: 'primary.main' }} />
          <Typography variant="h6" fontWeight={700}>
            {copy.start.title}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 520 }}>
            {copy.start.body(stages[0]?.docType.name ?? null)}
          </Typography>
          {canEdit && nextStage && (
            <Button
              variant="contained"
              size="large"
              startIcon={<AutoAwesomeIcon />}
              disabled={isBusy || starting}
              onClick={() => void startStage(nextStage)}
            >
              {copy.start.button}
            </Button>
          )}
        </GlassPanel>
      )}

      <EditSectionDialog section={editing} onClose={() => setEditing(null)} />
      <ConfirmDialog
        open={regenerateOpen}
        title="Regenerate this stage?"
        message={copy.regenerateMessage(selectedStage?.docType.name ?? 'This stage')}
        confirmLabel="Regenerate"
        loading={starting}
        onConfirm={() => void regenerate()}
        onClose={() => {
          if (!starting) setRegenerateOpen(false);
        }}
      />
    </Box>
  );
}
